import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, MapPin, Users, Power, Save } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useVenue } from "@/context/VenueContext";

export default function PlusManage() {
  const navigate = useNavigate();
  const { venues, reload, role } = useVenue();
  const [staff, setStaff] = useState([]);
  const [newVenue, setNewVenue] = useState("");
  const [nf, setNf] = useState({ email: "", password: "", nome: "", role: "SALA_OPERATOR", venue_ids: [] });
  const [busy, setBusy] = useState(false);

  const loadStaff = useCallback(async () => {
    try { const { data } = await api.get("/plus/staff"); setStaff(data); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { loadStaff(); }, [loadStaff]);

  const createVenue = async () => {
    if (!newVenue.trim()) return;
    setBusy(true);
    try { await api.post("/plus/venues", { nome: newVenue.trim() }); toast.success("Sala creata"); setNewVenue(""); await reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleVenue = async (v) => {
    try { await api.patch(`/plus/venues/${v.id}`, { stato: v.stato === "ACTIVE" ? "SUSPENDED" : "ACTIVE" }); await reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleNfVenue = (id) => setNf((s) => ({ ...s, venue_ids: s.venue_ids.includes(id) ? s.venue_ids.filter((x) => x !== id) : [...s.venue_ids, id] }));

  const createStaff = async () => {
    if (!nf.email || !nf.password || !nf.nome) { toast.error("Compila nome, email e password"); return; }
    setBusy(true);
    try {
      await api.post("/plus/staff", nf);
      toast.success("Operatore creato");
      setNf({ email: "", password: "", nome: "", role: "SALA_OPERATOR", venue_ids: [] });
      await loadStaff();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleStaffActive = async (s) => {
    try { await api.patch(`/plus/staff/${s.id}`, { attivo: !(s.attivo !== false) }); await loadStaff(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStaffVenues = async (s, venue_ids) => {
    try { await api.patch(`/plus/staff/${s.id}`, { venue_ids }); await loadStaff(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const venueName = (id) => venues.find((v) => v.id === id)?.nome || id;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center gap-3 mb-8">
          <button data-testid="manage-back" onClick={() => navigate("/")} className="p-2 rounded-lg border border-zinc-800 hover:border-zinc-600"><ArrowLeft className="w-4 h-4" /></button>
          <h1 className="font-display text-2xl font-bold">Gestione Sale &amp; Staff</h1>
          <span className="ml-auto text-xs text-zinc-500">{role === "PLATFORM_OWNER" ? "Platform Owner" : "Titolare"}</span>
        </div>

        {/* SALE */}
        <section className="mb-10" data-testid="manage-venues">
          <div className="flex items-center gap-2 mb-4"><MapPin className="w-5 h-5 text-sky-400" /><h2 className="font-display text-lg font-bold">Le tue sale</h2></div>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            {venues.map((v) => (
              <div key={v.id} data-testid={`manage-venue-${v.id}`} className="flex items-center gap-3 rounded-xl bg-zinc-900 border border-zinc-800 px-4 py-3">
                <span className={`w-2 h-2 rounded-full ${v.stato === "ACTIVE" ? "bg-emerald-400" : "bg-red-500"}`} />
                <span className="flex-1 font-medium">{v.nome}</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">{v.stato === "ACTIVE" ? "Attiva" : "Sospesa"}</span>
                <button data-testid={`venue-toggle-${v.id}`} onClick={() => toggleVenue(v)} className="p-1.5 rounded-lg border border-zinc-800 hover:border-zinc-600" title="Attiva/Sospendi"><Power className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="new-venue-input" value={newVenue} onChange={(e) => setNewVenue(e.target.value)} placeholder="Nome nuova sala…"
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm focus:border-sky-500 outline-none" />
            <button data-testid="create-venue-btn" disabled={busy} onClick={createVenue} className="flex items-center gap-2 px-4 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold text-sm disabled:opacity-50"><Plus className="w-4 h-4" /> Aggiungi sala</button>
          </div>
        </section>

        {/* STAFF */}
        <section data-testid="manage-staff">
          <div className="flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-amber-400" /><h2 className="font-display text-lg font-bold">Staff</h2></div>
          <div className="rounded-xl bg-zinc-900 border border-zinc-800 divide-y divide-zinc-800 mb-5">
            {staff.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500 text-center">Nessun operatore ancora.</div>}
            {staff.map((s) => (
              <div key={s.id} data-testid={`staff-${s.id}`} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="font-medium">{s.nome} <span className="text-xs text-zinc-500">· {s.role === "SALA_ADMIN" ? "Admin sala" : "Operatore"}</span></div>
                    <div className="text-xs text-zinc-500">{s.email}</div>
                  </div>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${s.attivo !== false ? "text-emerald-300 bg-emerald-500/10" : "text-red-300 bg-red-500/10"}`}>{s.attivo !== false ? "Attivo" : "Disattivo"}</span>
                  <button data-testid={`staff-toggle-${s.id}`} onClick={() => toggleStaffActive(s)} className="p-1.5 rounded-lg border border-zinc-800 hover:border-zinc-600"><Power className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {venues.map((v) => {
                    const on = (s.venue_ids || []).includes(v.id);
                    return (
                      <button key={v.id} data-testid={`staff-${s.id}-venue-${v.id}`}
                        onClick={() => setStaffVenues(s, on ? (s.venue_ids || []).filter((x) => x !== v.id) : [...(s.venue_ids || []), v.id])}
                        className={`text-[11px] px-2 py-1 rounded-lg border ${on ? "border-sky-500 bg-sky-500/15 text-sky-300" : "border-zinc-700 text-zinc-400"}`}>
                        {v.nome}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* nuovo operatore */}
          <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-4" data-testid="new-staff-form">
            <div className="text-sm font-semibold mb-3">Nuovo operatore</div>
            <div className="grid sm:grid-cols-3 gap-2 mb-3">
              <input data-testid="staff-nome" value={nf.nome} onChange={(e) => setNf({ ...nf, nome: e.target.value })} placeholder="Nome" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500" />
              <input data-testid="staff-email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder="Email" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500" />
              <input data-testid="staff-password" type="password" value={nf.password} onChange={(e) => setNf({ ...nf, password: e.target.value })} placeholder="Password (min 6)" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500" />
            </div>
            <div className="flex items-center gap-2 mb-3">
              <select data-testid="staff-role" value={nf.role} onChange={(e) => setNf({ ...nf, role: e.target.value })} className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none">
                <option value="SALA_OPERATOR">Operatore</option>
                <option value="SALA_ADMIN">Admin sala</option>
              </select>
              <span className="text-xs text-zinc-500">Sale assegnate:</span>
              <div className="flex flex-wrap gap-1.5">
                {venues.map((v) => {
                  const on = nf.venue_ids.includes(v.id);
                  return <button key={v.id} data-testid={`nf-venue-${v.id}`} onClick={() => toggleNfVenue(v.id)} className={`text-[11px] px-2 py-1 rounded-lg border ${on ? "border-sky-500 bg-sky-500/15 text-sky-300" : "border-zinc-700 text-zinc-400"}`}>{v.nome}</button>;
                })}
              </div>
            </div>
            <button data-testid="create-staff-btn" disabled={busy} onClick={createStaff} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm disabled:opacity-50"><Save className="w-4 h-4" /> Crea operatore</button>
          </div>
        </section>
      </div>
    </div>
  );
}
